/**
 * personalEvent.ts — 내 업무 홈의 "개인 일정" (B′ 미리보기, 2026-10-19).
 *
 * 저장소: users/{uid}/personal_events/{autoId}  (services/personalEvents.repository.ts)
 *   - 문서 ID = Firestore 자동 ID. 비즈니스 키 없음(같은 제목·날짜 일정이 여러 개일 수 있음).
 *   - 본인만 읽기·쓰기(firestore.rules `match /users/{uid}/personal_events/{eventId}`).
 *
 * 외부 캘린더(1차): Google 캘린더 "일정 추가" 링크 + .ics 파일 내려받기만.
 *   구독형 양방향 동기화는 만들지 않는다(조직 정책상 onRequest 불가 → 추후 트리거 패턴으로 별도 설계).
 *
 * 일정·할 일 (2026-10-19 2차): 같은 컬렉션에 `kind` 로 구분한다.
 *   - event : 일정(시각·종일)
 *   - todo  : 할 일(체크리스트). `date` = 할 날짜(캘린더에 놓이는 날), `done` = 완료 여부
 *   옛 문서(kind 없음)는 event 로 본다. kind 화이트리스트는 PERSONAL_EVENT_KINDS — union 에 값을 더하면
 *   여기와 firestore.rules `validEvent` 의 kind 목록을 같이 고친다.
 *
 * 순수 타입·계산만. Firebase import 금지(domain 규칙).
 */

export type PersonalEventKind = "event" | "todo";
/** 런타임 화이트리스트 — coercePersonalEvent·rules 와 동기 */
export const PERSONAL_EVENT_KINDS: readonly PersonalEventKind[] = ["event", "todo"] as const;
export function isPersonalEventKind(v: unknown): v is PersonalEventKind {
  return typeof v === "string" && (PERSONAL_EVENT_KINDS as readonly string[]).includes(v);
}

export interface PersonalEvent {
  /** Firestore 문서 ID (미리보기 로컬 저장이면 "local-…") */
  id: string;
  /** 일정 / 할 일 */
  kind: PersonalEventKind;
  /** 할 일 완료 여부 (일정은 항상 false) */
  done: boolean;
  title: string;
  /** "YYYY-MM-DD" */
  date: string;
  all_day: boolean;
  /** 속한 내 캘린더 문서 ID(users/{uid}/calendars). 없으면 기본 캘린더(domain/calendar DEFAULT_CALENDAR_ID) */
  calendar_id?: string;
  /** "HH:MM" (24시간제). all_day 면 없음 */
  start_time?: string;
  /** "HH:MM". 없으면 시작 시각만 */
  end_time?: string;
  memo?: string;
  /** epoch ms (Timestamp 를 변환해 둔다) */
  created_at?: number;
  updated_at?: number;
}

/** 폼 → 저장 입력 (id·시각 메타 제외) */
export type PersonalEventInput = Pick<
  PersonalEvent,
  "kind" | "done" | "title" | "date" | "all_day" | "start_time" | "end_time" | "memo" | "calendar_id"
>;

/** 저장된 항목 → 같은 내용의 입력(완료 토글 등 일부만 바꿔 다시 저장할 때) */
export function toEventInput(ev: PersonalEvent): PersonalEventInput {
  return {
    kind: ev.kind, done: ev.done, title: ev.title, date: ev.date, all_day: ev.all_day,
    start_time: ev.start_time, end_time: ev.end_time, memo: ev.memo, calendar_id: ev.calendar_id,
  };
}

export const EVENT_TITLE_MAX = 100;
export const EVENT_MEMO_MAX = 500;

const YMD_RE = /^\d{4}-\d{2}-\d{2}$/;
const HM_RE = /^([01]\d|2[0-3]):[0-5]\d$/;

export function isYmd(v: unknown): v is string {
  return typeof v === "string" && YMD_RE.test(v);
}
export function isHm(v: unknown): v is string {
  return typeof v === "string" && HM_RE.test(v);
}

function toMs(v: unknown): number | undefined {
  if (typeof v === "number" && Number.isFinite(v)) return v;
  if (v && typeof v === "object" && "toMillis" in v && typeof (v as { toMillis: unknown }).toMillis === "function") {
    return (v as { toMillis: () => number }).toMillis();
  }
  return undefined;
}

/**
 * 저장 문서 → PersonalEvent. 필수 필드(title·date)가 깨진 문서는 null(목록에서 제외).
 * 시간 필드가 형식에 안 맞으면 종일로 본다(조용한 잘못 표시 대신 보수적으로).
 * kind 가 없으면(옛 문서) event, 알 수 없는 값이면 문서를 제외한다(다른 종류로 잘못 보이지 않게).
 */
export function coercePersonalEvent(id: string, data: Record<string, unknown>): PersonalEvent | null {
  const title = typeof data.title === "string" ? data.title.trim() : "";
  if (!title || !isYmd(data.date)) return null;
  const kind: PersonalEventKind | null =
    data.kind === undefined ? "event" : isPersonalEventKind(data.kind) ? data.kind : null;
  if (!kind) return null;
  const start = isHm(data.start_time) ? data.start_time : undefined;
  const allDay = data.all_day === true || !start;
  const end = !allDay && isHm(data.end_time) ? data.end_time : undefined;
  const memo = typeof data.memo === "string" && data.memo.trim() ? data.memo.trim() : undefined;
  const calendarId = typeof data.calendar_id === "string" && data.calendar_id.trim() ? data.calendar_id.trim() : undefined;
  return {
    id,
    kind,
    calendar_id: calendarId,
    done: kind === "todo" && data.done === true,
    title,
    date: data.date,
    all_day: allDay,
    start_time: allDay ? undefined : start,
    end_time: end,
    memo,
    created_at: toMs(data.created_at),
    updated_at: toMs(data.updated_at),
  };
}

/** 폼 입력 검증 + 정리. 실패하면 사용자에게 보여줄 문장. memoMax = 메모 한도(Google 일정은 더 김) */
export function normalizeEventInput(
  input: PersonalEventInput,
  memoMax: number = EVENT_MEMO_MAX,
): { ok: true; value: PersonalEventInput } | { ok: false; error: string } {
  const title = input.title.trim();
  if (!title) return { ok: false, error: "제목을 입력하세요." };
  if (title.length > EVENT_TITLE_MAX) return { ok: false, error: `제목은 ${EVENT_TITLE_MAX}자까지 입력할 수 있습니다.` };
  if (!isPersonalEventKind(input.kind)) return { ok: false, error: "종류를 선택하세요." };
  if (!isYmd(input.date)) return { ok: false, error: "날짜를 선택하세요." };
  const kind = input.kind;
  const done = kind === "todo" && input.done === true;
  const memo = (input.memo ?? "").trim();
  if (memo.length > memoMax) return { ok: false, error: `메모는 ${memoMax}자까지 입력할 수 있습니다.` };
  // 기본 캘린더는 calendar_id 없이 저장(옛 문서와 같은 모양)
  const calendar_id = input.calendar_id && input.calendar_id !== "default" ? input.calendar_id : undefined;
  if (input.all_day) {
    return { ok: true, value: { kind, done, title, date: input.date, all_day: true, memo: memo || undefined, calendar_id } };
  }
  if (!isHm(input.start_time)) return { ok: false, error: "시작 시각을 입력하거나 '종일'을 선택하세요." };
  const end = isHm(input.end_time) ? input.end_time : undefined;
  if (end && end <= input.start_time) return { ok: false, error: "끝 시각은 시작 시각보다 늦어야 합니다." };
  return {
    ok: true,
    value: { kind, done, title, date: input.date, all_day: false, start_time: input.start_time, end_time: end, memo: memo || undefined, calendar_id },
  };
}

/** 날짜 → 일정 먼저(할 일 뒤) → 종일 먼저 → 시작 시각 → 제목 (실시간 목록은 서버 정렬 대신 여기서 정렬) */
export function sortEvents(list: PersonalEvent[]): PersonalEvent[] {
  return [...list].sort((a, b) => {
    if (a.date !== b.date) return a.date < b.date ? -1 : 1;
    if (a.kind !== b.kind) return a.kind === "event" ? -1 : 1;
    if (a.all_day !== b.all_day) return a.all_day ? -1 : 1;
    const sa = a.start_time ?? "";
    const sb = b.start_time ?? "";
    if (sa !== sb) return sa < sb ? -1 : 1;
    return a.title.localeCompare(b.title, "ko");
  });
}

/** "종일" / "09:00" / "09:00–10:30" */
export function eventTimeLabel(ev: Pick<PersonalEvent, "all_day" | "start_time" | "end_time">): string {
  if (ev.all_day || !ev.start_time) return "종일";
  return ev.end_time ? `${ev.start_time}–${ev.end_time}` : ev.start_time;
}

// ───────────────────────── 외부 캘린더 (1차) ─────────────────────────

const pad2 = (n: number) => String(n).padStart(2, "0");

/** YYYY-MM-DD 에 n일 더하기 (UTC 계산 — 시간대 무관) */
function addDaysYmd(ymd: string, n: number): string {
  const [y, m, d] = ymd.split("-").map(Number);
  const dt = new Date(Date.UTC(y, m - 1, d + n));
  return `${dt.getUTCFullYear()}-${pad2(dt.getUTCMonth() + 1)}-${pad2(dt.getUTCDate())}`;
}

const compactDate = (ymd: string) => ymd.replaceAll("-", "");

/** 시작·끝 (YYYY-MM-DD, HH:MM). 끝 시각이 없으면 1시간 뒤(자정 넘김 처리) */
function timedRange(ev: PersonalEvent): { sd: string; st: string; ed: string; et: string } {
  const st = ev.start_time ?? "09:00";
  if (ev.end_time) return { sd: ev.date, st, ed: ev.date, et: ev.end_time };
  const [h, mi] = st.split(":").map(Number);
  const nh = h + 1;
  return nh >= 24
    ? { sd: ev.date, st, ed: addDaysYmd(ev.date, 1), et: `${pad2(nh - 24)}:${pad2(mi)}` }
    : { sd: ev.date, st, ed: ev.date, et: `${pad2(nh)}:${pad2(mi)}` };
}

/** Google 캘린더 "일정 추가" 화면 링크 (새 창으로 연다 — 저장은 사용자가 Google 화면에서) */
export function googleCalendarUrl(ev: PersonalEvent): string {
  let dates: string;
  if (ev.all_day) {
    dates = `${compactDate(ev.date)}/${compactDate(addDaysYmd(ev.date, 1))}`;
  } else {
    const r = timedRange(ev);
    dates = `${compactDate(r.sd)}T${r.st.replace(":", "")}00/${compactDate(r.ed)}T${r.et.replace(":", "")}00`;
  }
  const p = new URLSearchParams({ action: "TEMPLATE", text: ev.title, dates, ctz: "Asia/Seoul" });
  if (ev.memo) p.set("details", ev.memo);
  return `https://calendar.google.com/calendar/render?${p.toString()}`;
}

/** iCalendar TEXT 이스케이프 (RFC 5545 §3.3.11) */
function icsText(s: string): string {
  return s.replace(/\\/g, "\\\\").replace(/;/g, "\\;").replace(/,/g, "\\,").replace(/\r?\n/g, "\\n");
}

/** KST(UTC+9, 서머타임 없음) 로컬 시각 → UTC "YYYYMMDDTHHMMSSZ" */
function kstToUtcStamp(ymd: string, hm: string): string {
  const [y, m, d] = ymd.split("-").map(Number);
  const [h, mi] = hm.split(":").map(Number);
  const dt = new Date(Date.UTC(y, m - 1, d, h - 9, mi));
  return `${dt.getUTCFullYear()}${pad2(dt.getUTCMonth() + 1)}${pad2(dt.getUTCDate())}T${pad2(dt.getUTCHours())}${pad2(dt.getUTCMinutes())}00Z`;
}

function nowUtcStamp(): string {
  const dt = new Date();
  return `${dt.getUTCFullYear()}${pad2(dt.getUTCMonth() + 1)}${pad2(dt.getUTCDate())}T${pad2(dt.getUTCHours())}${pad2(dt.getUTCMinutes())}${pad2(dt.getUTCSeconds())}Z`;
}

/** .ics 파일 본문 (VEVENT 1건). 시각은 UTC 로 적어 VTIMEZONE 없이도 어긋나지 않게 */
export function buildIcs(ev: PersonalEvent): string {
  const lines = [
    "BEGIN:VCALENDAR",
    "VERSION:2.0",
    "PRODID:-//JGIND RMC//Personal Event//KO",
    "CALSCALE:GREGORIAN",
    "BEGIN:VEVENT",
    `UID:${ev.id}@erp.jgind.co.kr`,
    `DTSTAMP:${nowUtcStamp()}`,
  ];
  if (ev.all_day) {
    lines.push(`DTSTART;VALUE=DATE:${compactDate(ev.date)}`, `DTEND;VALUE=DATE:${compactDate(addDaysYmd(ev.date, 1))}`);
  } else {
    const r = timedRange(ev);
    lines.push(`DTSTART:${kstToUtcStamp(r.sd, r.st)}`, `DTEND:${kstToUtcStamp(r.ed, r.et)}`);
  }
  lines.push(`SUMMARY:${icsText(ev.title)}`);
  if (ev.memo) lines.push(`DESCRIPTION:${icsText(ev.memo)}`);
  lines.push("END:VEVENT", "END:VCALENDAR");
  return lines.join("\r\n") + "\r\n";
}

/** 내려받을 파일 이름 — 파일명에 못 쓰는 문자는 _ */
export function icsFileName(ev: PersonalEvent): string {
  const safe = ev.title.replace(/[\\/:*?"<>|\s]+/g, "_").slice(0, 40) || "일정";
  return `${ev.date}_${safe}.ics`;
}
